import { useState, useRef, useEffect, useId } from 'react'
import { fileAPI } from '../services/api'
import './FileUpload.css'

function serverRecordFromProp(sf) {
  if (sf == null) return null
  if (typeof sf === 'string') {
    const id = sf
    return { id, local: false, name: id.length > 12 ? `${id.slice(0, 8)}…` : id }
  }
  const id = sf.id != null ? String(sf.id) : null
  if (!id) return null
  return {
    ...sf,
    id,
    local: false,
    name: sf.original_filename ?? sf.name ?? (id.length > 12 ? `${id.slice(0, 8)}…` : id),
  }
}

function filesToParentPayload(rows) {
  return rows
    .filter((f) => f.status === 'server' || f.status === 'staged')
    .map((f) => {
      if (f.status === 'server') {
        return f.serverRecord
      }
      return {
        local: true,
        tempId: f.id,
        file: f.file,
        name: f.name,
        size: f.size,
        mime_type: f.type,
      }
    })
}

function sourceFileTypeLabel(filename) {
  const base = (filename || '').toLowerCase()
  if (base.endsWith('.kicad_sch')) return 'KiCad schematic'
  if (base.endsWith('.kicad_pro')) return 'KiCad project'
  if (base.endsWith('.kicad_pcb')) return 'KiCad PCB'
  if (base.endsWith('.kicad_sym')) return 'KiCad symbol'
  if (base === 'easyeda-source.json' || base.endsWith('/easyeda-source.json')) return 'EasyEDA source'
  if (base.endsWith('.zip')) return 'Archive'
  if (base.endsWith('.json')) return 'JSON source'
  if (base.endsWith('.sch')) return 'Eagle schematic'
  if (base.endsWith('.brd')) return 'Eagle board'
  return 'Source file'
}

function fileDescriptionKey(fileItem) {
  if (fileItem.status === 'server' && fileItem.serverRecord?.id) return String(fileItem.serverRecord.id)
  if (fileItem.status === 'uploaded' && fileItem.uploadedFile?.id) return String(fileItem.uploadedFile.id)
  if (fileItem.status === 'staged' && fileItem.id) return fileItem.id
  return fileItem.id
}

function FileUpload({
  onFilesChange,
  maxFiles = 10,
  maxSizeMB = 50,
  /** Circuit notes uploads: Markdown only (.md / .markdown). */
  markdownNotesOnly = false,
  /** Schematic source uploads: KiCad, EasyEDA, Eagle, zip, json. */
  sourceFilesOnly = false,
  /** Optional short description per file (source uploads). */
  withDescriptions = false,
  descriptions = {},
  onDescriptionChange,
  deferUpload = false,
  serverFiles = null,
}) {
  const [files, setFiles] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const fileInputRef = useRef(null)
  const inputId = useId()

  useEffect(() => {
    setFiles((prev) => {
      if (deferUpload) {
        const staged = prev.filter((f) => f.status === 'staged')
        const serverRows = (serverFiles || [])
          .map((sf) => {
            const rec = serverRecordFromProp(sf)
            if (!rec) return null
            return {
              id: `server-${rec.id}`,
              status: 'server',
              name: rec.name,
              size: rec.size_bytes ?? rec.size ?? 0,
              serverRecord: rec,
            }
          })
          .filter(Boolean)
        return [...serverRows, ...staged]
      }

      const inFlight = prev.filter((f) =>
        f.status === 'pending' || f.status === 'uploading' || f.status === 'error'
      )
      const serverRows = (serverFiles || [])
        .map((sf) => {
          const rec = serverRecordFromProp(sf)
          if (!rec) return null
          return {
            id: `server-${rec.id}`,
            status: 'uploaded',
            name: rec.name,
            size: rec.size_bytes ?? rec.size ?? 0,
            type: rec.mime_type || '',
            uploadedFile: rec,
          }
        })
        .filter(Boolean)
      return [...serverRows, ...inFlight]
    })
  }, [deferUpload, serverFiles])

  const allowedTypes = markdownNotesOnly
    ? ['text/markdown', 'text/x-markdown']
    : sourceFilesOnly
      ? ['application/zip', 'application/x-zip-compressed', 'application/json', 'text/json']
      : ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'application/zip', 'application/x-zip-compressed']
  const validExtensions = markdownNotesOnly
    ? ['.md', '.markdown']
    : sourceFilesOnly
      ? ['.kicad_sch', '.kicad_pcb', '.kicad_pro', '.kicad_prl', '.kicad_sym', '.kicad_wks', '.sch', '.brd', '.zip', '.json']
      : ['.pdf', '.png', '.jpg', '.jpeg', '.svg', '.zip']
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  const validateFile = (file) => {
    const errors = []

    const fileType = file.type || ''
    const fileName = file.name.toLowerCase()
    const dot = fileName.lastIndexOf('.')
    const fileExtension = dot >= 0 ? fileName.substring(dot) : ''

    let isValidType
    if (markdownNotesOnly) {
      // Extension is authoritative: many browsers/OSes label .md as text/plain or application/octet-stream.
      isValidType =
        validExtensions.includes(fileExtension) ||
        allowedTypes.includes(fileType)
    } else {
      isValidType = allowedTypes.includes(fileType)
      if (!isValidType) {
        isValidType = validExtensions.includes(fileExtension)
      }
      if (fileType.startsWith('image/') && (fileExtension === '.jpg' || fileExtension === '.jpeg')) {
        isValidType = true
      }
    }

    if (!isValidType && sourceFilesOnly) {
      const lowerName = fileName
      isValidType =
        validExtensions.some((ext) => lowerName.endsWith(ext)) ||
        lowerName.endsWith('easyeda-source.json')
    }

    if (!isValidType) {
      errors.push(
        markdownNotesOnly
          ? `${file.name}: Circuit notes must be Markdown (.md or .markdown).`
          : sourceFilesOnly
            ? `${file.name}: Invalid source file type. Allowed: KiCad, EasyEDA, Eagle, .zip, .json`
            : `${file.name}: Invalid file type. Allowed: PDF, PNG, JPG, SVG, ZIP (detected: ${fileType || 'unknown'})`
      )
    }

    if (file.size > maxSizeBytes) {
      errors.push(`${file.name}: File size exceeds ${maxSizeMB}MB limit`)
    }

    return errors
  }

  const handleFileSelect = async (selectedFiles) => {
    const fileArray = Array.from(selectedFiles)
    const errors = []
    const validFiles = []

    for (const file of fileArray) {
      const fileErrors = validateFile(file)
      if (fileErrors.length > 0) {
        errors.push(...fileErrors)
      } else {
        validFiles.push(file)
      }
    }

    if (files.length + validFiles.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`)
    }

    if (errors.length > 0) {
      alert(errors.join('\n'))
      return
    }

    if (validFiles.length === 0) {
      return
    }

    if (deferUpload) {
      setFiles((prev) => {
        const serverPart = prev.filter((f) => f.status === 'server')
        const stagedPart = prev.filter((f) => f.status === 'staged')
        const newStaged = validFiles.map((file, index) => ({
          id: `staged-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${index}`}`,
          status: 'staged',
          file,
          name: file.name,
          size: file.size,
          type: file.type,
        }))
        const next = [...serverPart, ...stagedPart, ...newStaged]
        if (onFilesChange) {
          onFilesChange(filesToParentPayload(next))
        }
        return next
      })
      return
    }

    const newFiles = validFiles.map((file, index) => ({
      id: Date.now() + index + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      uploadedFile: null,
      error: null,
    }))

    setFiles((prev) => [...prev, ...newFiles])

    setUploading(true)
    try {
      for (const fileItem of newFiles) {
        await uploadFile(fileItem)
      }
    } catch (error) {
      console.error('Error uploading files:', error)
    } finally {
      setUploading(false)
    }
  }

  const uploadFile = async (fileItem) => {
    try {
      setFiles((prev) => prev.map((f) =>
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ))

      const response = await fileAPI.upload(
        fileItem.file,
        (progress) => {
          setUploadProgress((prev) => ({ ...prev, [fileItem.id]: progress }))
        },
        { circuitNotes: markdownNotesOnly, sourceFiles: sourceFilesOnly }
      )

      setFiles((prev) => {
        const updated = prev.map((f) =>
          f.id === fileItem.id
            ? { ...f, status: 'uploaded', uploadedFile: response.file }
            : f
        )

        if (onFilesChange) {
          const uploadedFiles = updated
            .filter((f) => f.status === 'uploaded' && f.uploadedFile)
            .map((f) => f.uploadedFile)
          onFilesChange(uploadedFiles)
        }

        return updated
      })
    } catch (error) {
      setFiles((prev) => prev.map((f) =>
        f.id === fileItem.id
          ? { ...f, status: 'error', error: error.message }
          : f
      ))
    }
  }

  const removeFile = (fileId) => {
    setFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId)

      if (onFilesChange) {
        if (deferUpload) {
          onFilesChange(filesToParentPayload(updated))
        } else {
          const uploadedFiles = updated
            .filter((f) => f.status === 'uploaded')
            .map((f) => f.uploadedFile)
          onFilesChange(uploadedFiles)
        }
      }

      return updated
    })
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = e.dataTransfer.files
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles)
    }
  }

  const handleFileInputChange = (e) => {
    const selectedFiles = e.target.files
    if (selectedFiles && selectedFiles.length > 0) {
      handleFileSelect(selectedFiles)
    }
    if (e.target) {
      e.target.value = ''
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const dropzoneBusy = deferUpload ? files.length >= maxFiles : (uploading || files.length >= maxFiles)

  return (
    <div className="file-upload">
      <label
        className={`file-upload-dropzone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ cursor: dropzoneBusy ? 'not-allowed' : 'pointer' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={
            markdownNotesOnly
              ? '.md,.markdown'
              : sourceFilesOnly
                ? '.kicad_sch,.kicad_pcb,.kicad_pro,.kicad_prl,.kicad_sym,.kicad_wks,.sch,.brd,.zip,.json'
                : '.pdf,.png,.jpg,.jpeg,.svg,.zip,image/*'
          }
          onChange={handleFileInputChange}
          className="file-upload-input"
          disabled={dropzoneBusy}
          id={inputId}
        />
        <div className="file-upload-content">
          <svg className="file-upload-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p className="file-upload-text">
            {uploading
              ? 'Uploading...'
              : isDragging
                ? 'Drop files here'
                : deferUpload
                  ? 'Click or drag to attach files'
                  : 'Click or drag files to upload'}
          </p>
          <p className="file-upload-hint">
            {markdownNotesOnly
              ? 'Markdown (.md) only'
              : sourceFilesOnly
                ? 'KiCad is the official supported source package (more types on the way)'
                : 'PDF, PNG, JPG, SVG, ZIP'}{' '}
            (max {maxSizeMB}MB per file, {maxFiles} files max)
            {deferUpload && (
              <span className="file-upload-defer-hint"> · Files upload when you submit for review</span>
            )}
          </p>
        </div>
      </label>

      {files.length > 0 && (
        <div className="file-upload-list">
          {files.map((fileItem) => (
            <div
              key={fileItem.id}
              className={`file-upload-item file-upload-item-${fileItem.status}`}
            >
              <div className="file-upload-item-info">
                <span className="file-upload-item-name">{fileItem.name}</span>
                {sourceFilesOnly && (
                  <span className="file-upload-item-type">{sourceFileTypeLabel(fileItem.name)}</span>
                )}
                <span className="file-upload-item-size">{formatFileSize(fileItem.size)}</span>
                {withDescriptions && onDescriptionChange && (
                  <input
                    type="text"
                    className="file-upload-description-input"
                    placeholder="Short description (optional)"
                    value={descriptions[fileDescriptionKey(fileItem)] || ''}
                    onChange={(e) => onDescriptionChange(fileDescriptionKey(fileItem), e.target.value)}
                    maxLength={200}
                  />
                )}
              </div>
              <div className="file-upload-item-status">
                {fileItem.status === 'uploading' && (
                  <div className="file-upload-progress">
                    <div
                      className="file-upload-progress-bar"
                      style={{ width: `${uploadProgress[fileItem.id] || 0}%` }}
                    ></div>
                    <span>{Math.round(uploadProgress[fileItem.id] || 0)}%</span>
                  </div>
                )}
                {fileItem.status === 'uploaded' && (
                  <span className="file-upload-success">✓ Uploaded</span>
                )}
                {fileItem.status === 'server' && (
                  <span className="file-upload-server">Saved</span>
                )}
                {fileItem.status === 'staged' && (
                  <span className="file-upload-staged">Queued until submit</span>
                )}
                {fileItem.status === 'error' && (
                  <span className="file-upload-error" title={fileItem.error}>✗ Error</span>
                )}
                {fileItem.status === 'pending' && (
                  <span className="file-upload-pending">Pending...</span>
                )}
              </div>
              <button
                className="file-upload-remove"
                onClick={(e) => {
                  e.stopPropagation()
                  removeFile(fileItem.id)
                }}
                disabled={fileItem.status === 'uploading'}
                aria-label="Remove file"
                type="button"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FileUpload
